import { type FC, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpecialEvent, useAuth } from '@/hooks';
import { useAddonWaitlist } from '@/hooks/data/useAddonWaitlist';
import { supabase } from '@/lib/api/supabase';
import type { Addon, DbAddon } from '@/types';
import { dbToAddon } from '@/types';
import {
  HeroSection,
  EventInfoSection,
  LocationSection,
  ItinerarySection,
  DressCodeSection,
  GallerySection,
  RegistrationCTA,
} from '@/components/special-event';

/**
 * Public-facing special event page
 * Displays the active special event with all its sections
 */
export const SpecialEventPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { specialEvents, isLoading, error } = useSpecialEvent();
  const {
    joinWaitlist: joinAddonWaitlist,
    leaveWaitlist: leaveAddonWaitlist,
    getUserWaitlistEntry: getAddonWaitlistEntry,
    loading: waitlistLoading,
  } = useAddonWaitlist();

  // Display the first active special event (sorted by date)
  const specialEvent = specialEvents.length > 0 ? specialEvents[0] : null;

  // Linked addon state for sold-out detection
  const [linkedAddon, setLinkedAddon] = useState<Addon | null>(null);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number | null>(null);
  const [_waitlistError, setWaitlistError] = useState<string | null>(null);
  const actionInFlight = useRef(false);

  // Fetch linked addon for sold-out detection
  useEffect(() => {
    if (!specialEvent?.linkedAddonId) {
      setLinkedAddon(null);
      return;
    }

    let cancelled = false;
    const fetchAddon = async () => {
      const { data } = await supabase
        .from('addons')
        .select('*')
        .eq('id', specialEvent.linkedAddonId!)
        .single();

      if (!cancelled && data) {
        setLinkedAddon(dbToAddon(data as DbAddon));
      }
    };

    void fetchAddon();
    return () => { cancelled = true; };
  }, [specialEvent?.linkedAddonId]);

  // Check waitlist status when user or event/addon changes
  useEffect(() => {
    if (!user || !specialEvent?.linkedAddonId) {
      setIsOnWaitlist(false);
      setWaitlistPosition(null);
      return;
    }

    let cancelled = false;
    const checkWaitlist = async () => {
      const entry = await getAddonWaitlistEntry(specialEvent.linkedAddonId!);
      if (cancelled) return;
      if (entry) {
        setIsOnWaitlist(true);
        setWaitlistPosition(entry.position);
      } else {
        setIsOnWaitlist(false);
        setWaitlistPosition(null);
      }
    };

    void checkWaitlist();
    return () => { cancelled = true; };
  }, [user, specialEvent?.linkedAddonId, getAddonWaitlistEntry]);

  const isSoldOut: boolean =
    !!linkedAddon
    && linkedAddon.hasInventory
    && linkedAddon.stockQuantity !== null
    && linkedAddon.stockQuantity <= 0;

  const handleRegisterClick = (): void => {
    if (!user) {
      navigate('/login', {
        state: { returnTo: '/event-registration' },
      });
      return;
    }
    navigate('/event-registration');
  };

  const handleJoinWaitlist = useCallback(async () => {
    if (!specialEvent?.linkedAddonId || actionInFlight.current) return;
    actionInFlight.current = true;
    setWaitlistError(null);
    try {
      const result = await joinAddonWaitlist(specialEvent.linkedAddonId);
      if (result.success) {
        setIsOnWaitlist(true);
        setWaitlistPosition(result.position ?? null);
      } else {
        setWaitlistError(result.error ?? 'Failed to join waitlist');
      }
    } finally {
      actionInFlight.current = false;
    }
  }, [specialEvent?.linkedAddonId, joinAddonWaitlist]);

  const handleLeaveWaitlist = useCallback(async () => {
    if (!specialEvent?.linkedAddonId || actionInFlight.current) return;
    actionInFlight.current = true;
    setWaitlistError(null);
    try {
      const result = await leaveAddonWaitlist(specialEvent.linkedAddonId);
      if (result.success) {
        setIsOnWaitlist(false);
        setWaitlistPosition(null);
      } else {
        setWaitlistError(result.error ?? 'Failed to leave waitlist');
      }
    } finally {
      actionInFlight.current = false;
    }
  }, [specialEvent?.linkedAddonId, leaveAddonWaitlist]);

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
            <p className="mt-4 text-white">Loading special event...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="rounded-lg bg-red-900/40 border border-red-500/40 p-6 text-red-200 backdrop-blur-md shadow-lg inline-block max-w-md">
              <p className="font-semibold mb-2">Error loading special event</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No active event state
  if (!specialEvent) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center py-20">
            <h1 className="text-4xl font-viking text-white mb-4">No Active Special Event</h1>
            <p className="text-lg text-orange-200 mb-8">
              There is currently no special event scheduled. Check back later for updates!
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-orange-500 text-white hover:bg-orange-600 px-6 py-3 rounded-md font-medium transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render all sections when event is available
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <HeroSection event={specialEvent} isSoldOut={isSoldOut} />

      {/* Event Information */}
      <EventInfoSection event={specialEvent} />

      {/* Itinerary/Schedule */}
      <ItinerarySection event={specialEvent} />

      {/* Dress Code */}
      <DressCodeSection event={specialEvent} />

      {/* Location Details */}
      <LocationSection event={specialEvent} />

      {/* Gallery */}
      <GallerySection event={specialEvent} />

      {/* Registration CTA */}
      <RegistrationCTA
        event={specialEvent}
        isSoldOut={isSoldOut}
        onRegisterClick={handleRegisterClick}
        onJoinWaitlistClick={handleJoinWaitlist}
        onLeaveWaitlistClick={handleLeaveWaitlist}
        waitlistPosition={waitlistPosition}
        isOnWaitlist={isOnWaitlist}
        isLoggedIn={!!user}
        waitlistLoading={waitlistLoading}
      />
    </div>
  );
};
